<template>
  <div class="solar-arkanoid-wrapper">
    <div v-if="gameState === 'MENU'" class="overlay">
      <h1>Fotón Rebotón</h1>
      <p>
        Maneja un espejo para guiar al fotón rebotón y conseguir generar energía con tus paneles solares<br>
        Usa las flechas ◀ ▶ o los botones.
      </p>
      <button
        class="btn-start"
        :disabled="!phaserReady"
        @click="startGame"
      >
        {{ phaserReady ? "EMPEZAR" : "CARGANDO..." }}
      </button>
    </div>

    <div v-if="gameState !== 'MENU'" class="hud">
      <div class="stat">ENERGIA: {{ score }} MWh</div>
      <div class="stat" :class="{ 'timer-low': timeLeft <= 10 }">
        TIEMPO: {{ timeLeft }}s
      </div>
    </div>

    <div id="game-container" ref="gameContainer">
      <div v-if="gameState === 'GAMEOVER'" class="overlay result">
        <h2>FIN DEL TIEMPO</h2>
        <div class="final-score">{{ score }}</div>
        <p>MWh generados</p>

        <p v-if="verifiedScore !== null" style="color: #00ff44">
          Verificado: {{ verifiedScore }} MWh
        </p>

        <p v-if="verifiedMoney !== null" style="color: #00ff44">
          Dinero ganado: {{ Number(verifiedMoney).toLocaleString("es-ES") }} €
        </p>

        <button class="btn-start" @click="$emit('volver-menu')">
          VOLVER AL MENÚ
        </button>
      </div>
    </div>

    <div class="mobile-controls">
      <button
        class="ctrl-btn"
        aria-label="Mover espejo a la izquierda"
        @pointerdown.prevent="isMovingLeft = true"
        @pointerup.prevent="isMovingLeft = false"
        @pointercancel.prevent="isMovingLeft = false"
        @pointerleave="isMovingLeft = false"
        @contextmenu.prevent
      >
        ◀
      </button>

      <button
        class="ctrl-btn"
        aria-label="Mover espejo a la derecha"
        @pointerdown.prevent="isMovingRight = true"
        @pointerup.prevent="isMovingRight = false"
        @pointercancel.prevent="isMovingRight = false"
        @pointerleave="isMovingRight = false"
        @contextmenu.prevent
      >
        ▶
      </button>
    </div>
  </div>
</template>

<script>
import * as Phaser from "phaser";
import axios from "@/axios";
import CryptoJS from "crypto-js";
import { useGameStore } from "@/stores/game";
import Swal from "sweetalert2";

const GAME_WIDTH = 500;
const GAME_HEIGHT = 600;

export default {
  name: "SolarArkanoid",

  data() {
    return {
      store: useGameStore(),

      game: null,
      scene: null,
      resizeObserver: null,
      gameState: "MENU",
      score: 0,
      timeLeft: 40,
      timerInterval: null,
      phaserReady: false,

      idPartida: null,
      claveTemporal: null,
      verifiedScore: null,
      verifiedMoney: null,

      statsInternas: {
        startTime: null,
        totalTimeMs: 0,
        paneles_completados: 0,
        rebotes_panel: 0,
      },

      COLORS: [0x555555, 0xFFD700, 0xFFAA00, 0xFFFF00],
      isMovingLeft: false,
      isMovingRight: false,
    };
  },

  mounted() {
    this.$nextTick(() => {
      this.initPhaser();
      //this.initResizeObserver();
    });
  },

  beforeDestroy() {
  clearInterval(this.timerInterval);

  if (this.game) {
    this.game.destroy(true);
    this.game = null;
  }

  this.scene = null;
},

  methods: {
    /*initResizeObserver() {
      const container = this.$refs.gameContainer;

      if (!container || typeof ResizeObserver === "undefined") return;

      this.resizeObserver = new ResizeObserver(() => {
        this.game?.scale?.refresh();
      });

      this.resizeObserver.observe(container);
    },*/

    initPhaser() {
      const self = this;
      const container = this.$refs.gameContainer;

      const config = {
        type: Phaser.AUTO,
        parent: container,
        backgroundColor: "#87CEEB",
        scale: {
          mode: Phaser.Scale.FIT,
          autoCenter: Phaser.Scale.CENTER_BOTH,
          width: GAME_WIDTH,
          height: GAME_HEIGHT,
        },
        physics: {
          default: "arcade",
          arcade: { debug: false },
        },
        input: {
          keyboard: true,
          windowEvents: true,
        },
        scene: {
          preload() {
            let g = this.make.graphics();

            // Paddle
            g.fillStyle(0xFFD700).fillRect(0, 0, 100, 15);
            g.generateTexture("paddle", 100, 15);
            g.clear();

            // Fotón
            g.fillStyle(0xffff00).fillCircle(10, 10, 10);
            g.generateTexture("ball", 20, 20);
            g.clear();

            // Paneles y fondo
            this.load.image("block", "minigames/foton/panel.png");
            this.load.image("background", "minigames/foton/fondo.png");
          },

          create() {
            self.scene = this;

            const bg = this.add.image(
              GAME_WIDTH / 2,
              GAME_HEIGHT / 2,
              "background"
            );

            bg.setDisplaySize(GAME_WIDTH, GAME_HEIGHT);

            this.cursors = this.input.keyboard.createCursorKeys();

            self.phaserReady = true;
          },

          update() {
            if (self.gameState === "PLAYING") {
              self.updatePaddleLogic(this.cursors);
            }
          },
        },
      };

      this.game = new Phaser.Game(config);
    },

    updatePaddleLogic(cursors) {
      const scene = this.scene;

      if (!scene || !scene.paddle || !cursors) return;

      const speed = 500;

      const moveLeft = cursors.left.isDown || this.isMovingLeft;
      const moveRight = cursors.right.isDown || this.isMovingRight;

      if (moveLeft) {
        scene.paddle.setVelocityX(-speed);
      } else if (moveRight) {
        scene.paddle.setVelocityX(speed);
      } else {
        scene.paddle.setVelocityX(0);
      }
    },

    async startGame() {
      if (!this.phaserReady || !this.scene) {
        return;
      }
      try {
        const response = await axios.post("/api/minigames/partida/iniciar", {
          game_id: "foton",
        });

        if (response.data.status !== "success") return;

        this.idPartida = response.data.data.id_partida;
        this.claveTemporal = response.data.data.clave_temporal;

        this.gameState = "PLAYING";
        this.score = 0;
        this.timeLeft = 60;
        this.verifiedScore = null;
        this.verifiedMoney = null;
        this.isMovingLeft = false;
        this.isMovingRight = false;

        this.statsInternas = {
          startTime: Date.now(),
          totalTimeMs: 0,
          paneles_completados: 0,
          rebotes_panel: 0,
        };

        clearInterval(this.timerInterval);

        if (this.game && this.game.canvas) {
          this.game.canvas.focus();
        }

        const scene = this.scene;

        scene.physics.resume();

        // Limpieza defensiva por si quedase algo de una partida anterior
        if (scene.ball) scene.ball.destroy();
        if (scene.paddle) scene.paddle.destroy();
        if (scene.blocks) scene.blocks.clear(true, true);

        const W = GAME_WIDTH;
        const H = GAME_HEIGHT;

        // Crear pala
        scene.paddle = scene.physics.add.image(W / 2, H / 4, "paddle").setImmovable();
        scene.paddle.setCollideWorldBounds(true);

        // Crear fotón en una posición aleatoria
        const ballX = Phaser.Math.Between(60, W - 60);
        const ballY = Phaser.Math.Between(180, 300);

        const velocityX = Phaser.Math.Between(150, 250) *
          (Math.random() < 0.5 ? -1 : 1);

        scene.ball = scene.physics.add.image(ballX, ballY, "ball");
        scene.ball.setCollideWorldBounds(true);
        scene.ball.setBounce(1);
        scene.ball.setVelocity(velocityX, -250);

        // Crear paneles
        scene.blocks = scene.physics.add.staticGroup();

        const blockWidth = 55;
        const totalBlocksWidth = 8 * blockWidth;
        const startX = (W - totalBlocksWidth) / 2 + 25;
        const floorY = H - 80;

        for (let x = 0; x < 8; x++) {
          let block = scene.blocks.create(startX + x * blockWidth, floorY, "block");

          block.setDisplaySize(50, 30);
          block.setData("level", 0);
          block.setData("active", true);
          block.setTint(this.COLORS[0]);
        }

        // Rebote con pala
        scene.physics.add.collider(scene.ball, scene.paddle, (ball, paddle) => {
          let diff = ball.x - paddle.x;
          ball.setVelocityX(10 * diff);
        });

        // Rebote con paneles
        scene.physics.add.collider(scene.ball, scene.blocks, (ball, block) => {
          if (!block.getData("active")) return;

          this.statsInternas.rebotes_panel++;

          let level = block.getData("level");

          if (level < 3) {
            level++;
            block.setData("level", level);
            block.setTint(this.COLORS[level]);

            if (level === 3) {
              this.score++;
              this.statsInternas.paneles_completados++;

              block.setData("active", false);
              block.setAlpha(0.3);

              scene.time.delayedCall(2000, () => {
                if (
                  this.gameState === "PLAYING" &&
                  block &&
                  block.scene
                ) {
                  block.setData("level", 0);
                  block.setData("active", true);
                  block.setTint(this.COLORS[0]);
                  block.setAlpha(1);
                }
              });
            }
          }
        });

        this.timerInterval = setInterval(() => {
          this.timeLeft--;

          if (this.timeLeft <= 0) {
            this.endGame();
          }
        }, 1000);
      } catch (error) {
        console.error("Error iniciando partida", error);

        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.data?.message ||
          "No se pudo iniciar la partida.";

       await Swal.fire({
        icon: "info",
        title: "Minijuego no disponible",
        text: msg,
        confirmButtonText: "Entendido",
        customClass: {
          confirmButton: "clausulazo-swal-confirm",
        },
      });

        this.gameState = "MENU";
      }
    },

    async endGame() {
      if (this.gameState === "GAMEOVER") return;

      this.gameState = "GAMEOVER";
      clearInterval(this.timerInterval);

      this.isMovingLeft = false;
      this.isMovingRight = false;

      if (this.scene) {
        this.scene.physics.pause();

        if (this.scene.ball) this.scene.ball.destroy();
        if (this.scene.paddle) this.scene.paddle.destroy();
        if (this.scene.blocks) this.scene.blocks.clear(true, true);
      }

      this.statsInternas.totalTimeMs = Date.now() - this.statsInternas.startTime;

      const stats = {
        paneles_completados: this.statsInternas.paneles_completados,
        rebotes_panel: this.statsInternas.rebotes_panel,
        tiempo_jugado_ms: this.statsInternas.totalTimeMs,
        puntos_locales: this.score,
      };

      await this.finalizarPartida(stats);
    },

    async finalizarPartida(stats) {
      if (!this.claveTemporal) return;

      const statsOrdenados = Object.keys(stats)
        .sort()
        .reduce((obj, key) => {
          obj[key] = stats[key];
          return obj;
        }, {});

      const statsStr = JSON.stringify(statsOrdenados);

      const hash = CryptoJS.HmacSHA256(statsStr, this.claveTemporal).toString(
        CryptoJS.enc.Hex
      );

      try {
        const response = await axios.post("/api/minigames/partida/finalizar", {
          id_partida: this.idPartida,
          stats,
          hash,
        });

        if (response.data.status === "success") {
          this.verifiedScore = response.data.data.score;
          this.verifiedMoney = response.data.data.dinero;

          await Promise.all([
            this.store.refreshStats(),
            this.store.refreshCompany(),
          ]);
        }
      } catch (error) {
        console.error("Error al validar partida", error);
      }
    },

    resetGame() {
      this.gameState = "MENU";
      this.score = 0;
      this.timeLeft = 60;
      this.verifiedScore = null;
      this.verifiedMoney = null;
      this.idPartida = null;
      this.claveTemporal = null;
      this.isMovingLeft = false;
      this.isMovingRight = false;
    },
  },
};
</script>

<style scoped>
.btn-start:disabled {
  opacity: 0.65;
  cursor: wait;
  transform: none;
  box-shadow: 0 5px 0 #bc9c0d;
}
.solar-arkanoid-wrapper,
.solar-arkanoid-wrapper * {
  box-sizing: border-box;
}

.solar-arkanoid-wrapper {
  width: 100%;
  max-width: 500px;
  height: auto;
  margin: 0 auto;
  position: relative;
  display: flex;
  flex-direction: column;
  background: #87ceeb;
  border: clamp(4px, 1.5vw, 8px) solid #2c3e50;
  border-radius: clamp(10px, 3vw, 15px);
  overflow: hidden;
  user-select: none;
  font-family: sans-serif;
  -webkit-tap-highlight-color: transparent;
}

#game-container {
  position: relative;
  width: 100%;
  height: auto;
  aspect-ratio: 5 / 6;
  flex: 0 0 auto;
  overflow: hidden;
  background: #87ceeb;
}

/* Phaser conserva una resolución lógica de 500x600 y escala el canvas. */
#game-container :deep(canvas) {
  display: block;
  max-width: 100%;
  outline: none;
  touch-action: none;
}

.mobile-controls {
  width: 100%;
  height: clamp(72px, 18vw, 100px);
  min-height: 72px;
  flex: 0 0 auto;
  background: #222;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: clamp(18px, 10vw, 70px);
  padding: clamp(8px, 2.5vw, 12px);
}

.ctrl-btn {
  width: min(38%, 120px);
  height: clamp(50px, 14vw, 64px);
  flex: 0 1 120px;
  font-size: clamp(1.5rem, 7vw, 2rem);
  line-height: 1;
  background: #444;
  color: white;
  border: none;
  border-bottom: 5px solid #000;
  border-radius: 12px;
  cursor: pointer;
  touch-action: none;
}

.ctrl-btn:active {
  transform: translateY(3px);
  border-bottom-width: 2px;
  background: #666;
}

.overlay {
  position: absolute;
  inset: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  padding: clamp(16px, 5vw, 28px);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.overlay h1,
.overlay h2 {
  max-width: 100%;
  margin: 0 0 12px;
  font-size: clamp(1.55rem, 7vw, 2.25rem);
  line-height: 1.15;
}

.overlay p {
  max-width: 420px;
  margin: 8px 0;
  font-size: clamp(0.88rem, 3.7vw, 1rem);
  line-height: 1.45;
}

.hud {
  position: absolute;
  top: clamp(8px, 3vw, 15px);
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: clamp(6px, 3vw, 18px);
  padding: 0 clamp(6px, 2vw, 12px);
  z-index: 50;
  pointer-events: none;
}

.stat {
  min-width: 0;
  background: rgba(255, 255, 255, 0.92);
  color: #333;
  padding: clamp(6px, 2vw, 8px) clamp(9px, 4vw, 20px);
  border-radius: 20px;
  font-size: clamp(0.68rem, 3vw, 1rem);
  font-weight: bold;
  line-height: 1.1;
  white-space: nowrap;
  box-shadow: 0 4px 0 rgba(0, 0, 0, 0.2);
}

.timer-low {
  color: #e74c3c;
  animation: blink 0.5s infinite alternate;
}

@keyframes blink {
  from {
    opacity: 1;
  }
  to {
    opacity: 0.5;
  }
}

.btn-start {
  max-width: 100%;
  background: #f1c40f;
  border: none;
  padding: clamp(11px, 3vw, 15px) clamp(24px, 9vw, 40px);
  font-size: clamp(1rem, 4.5vw, 1.3rem);
  font-weight: bold;
  border-radius: 30px;
  cursor: pointer;
  margin-top: clamp(12px, 4vw, 20px);
  box-shadow: 0 5px 0 #bc9c0d;
}

.btn-start:active {
  transform: translateY(3px);
  box-shadow: 0 2px 0 #bc9c0d;
}

.final-score {
  font-size: clamp(3.2rem, 16vw, 5rem);
  line-height: 1;
  color: #f1c40f;
  margin: 10px 0;
}

@media (max-width: 420px) {
  .mobile-controls {
    min-height: 68px;
    height: 72px;
  }

  .ctrl-btn {
    height: 50px;
    border-bottom-width: 4px;
  }

  .hud {
    justify-content: space-between;
  }
}

@media (orientation: landscape) and (max-height: 520px) {
  .solar-arkanoid-wrapper {
    max-width: min(500px, calc(83.333dvh - 60px));
  }

  .mobile-controls {
    height: 58px;
    min-height: 58px;
    padding: 5px 8px;
  }

  .ctrl-btn {
    height: 44px;
    font-size: 1.35rem;
  }

  .overlay {
    padding: 10px 16px;
  }

  .overlay h1,
  .overlay h2 {
    margin-bottom: 6px;
    font-size: 1.35rem;
  }

  .overlay p {
    margin: 4px 0;
    font-size: 0.8rem;
  }

  .btn-start {
    margin-top: 8px;
    padding: 9px 24px;
    font-size: 0.95rem;
  }
}
</style>
